import React, { useState, useEffect } from "react";
import { useParams, useLocation } from "react-router-dom";
import Swal from "sweetalert2";
import { useDispatch, useSelector } from "react-redux";
import MainLayout from "../../components/layout/MainLayout";
import Breadcrumbs from "../../components/common/Breadcrumbs";
import DrpClientFeedbackForm from "./DrpClientFeedbackForm";
import {
  fetchDrpClientFeedback,
  updateDrpClientFeedback,
  clearCurrentDrpClientFeedback,
} from "../../store/slices/drpClientFeedbackSlice";
import { fetchPatient } from "../../store/slices/patientsSlice";

const toDate = (v) => (v ? String(v).substring(0, 10) : "");

const EditDrpClientFeedback = () => {
  const dispatch = useDispatch();
  const { id } = useParams();
  const { state } = useLocation();
  const { caseId, patientId } = state || {};

  const { currentDrpClientFeedback, isLoading, error } = useSelector((s) => s.drpClientFeedback);

  const [formData, setFormData] = useState({ patient_id: patientId || "", case_id: caseId || "" });
  const [patientData, setPatientData] = useState(null);

  useEffect(() => {
    if (id) dispatch(fetchDrpClientFeedback(id));
    return () => dispatch(clearCurrentDrpClientFeedback());
  }, [dispatch, id]);

  useEffect(() => {
    const rec = currentDrpClientFeedback;
    if (rec && rec.id === parseInt(id)) {
      const { patient, case: caseRel, creator, updater, deleter, created_at, updated_at, deleted_at, ...scalars } = rec;
      setFormData({
        ...scalars,
        date_of_admission: toDate(scalars.date_of_admission),
        date_of_discharge: toDate(scalars.date_of_discharge),
        review_date: toDate(scalars.review_date),
      });
      if (rec.patient_id) {
        dispatch(fetchPatient(rec.patient_id)).unwrap().then((p) => setPatientData(p)).catch(() => {});
      }
    }
  }, [currentDrpClientFeedback, id, dispatch]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await dispatch(updateDrpClientFeedback({ id, formData })).unwrap();
      Swal.fire({ title: "Success!", text: "DRP Client Feedback updated successfully.", icon: "success", timer: 2000, showConfirmButton: false });
    } catch (err) {
      let msg = "Failed to update DRP Client Feedback";
      if (typeof err === "string") msg = err;
      else if (typeof err === "object" && err !== null) msg = Object.values(err).flat().join("\n");
      Swal.fire({ title: "Error", text: msg, icon: "error" });
    }
  };

  return (
    <MainLayout>
      <div className="container-xxl flex-grow-1 container-p-y">
        <Breadcrumbs />
        {patientData && (
          <div className="card mb-4">
            <div className="card-body bg-light-primary rounded-4">
              <div className="row g-3">
                <div className="col-md-3"><span className="fw-semibold text-secondary small d-block">Client Name:</span><span className="fw-bold text-dark">{patientData.first_name} {patientData.last_name}</span></div>
                <div className="col-md-3"><span className="fw-semibold text-secondary small d-block">MR Number:</span><span className="fw-bold text-dark">{patientData.mr_no || "N/A"}</span></div>
                <div className="col-md-3"><span className="fw-semibold text-secondary small d-block">Date of Birth:</span><span className="fw-bold text-dark">{patientData.dob ? new Date(patientData.dob).toLocaleDateString("en-GB") : "N/A"}</span></div>
                <div className="col-md-3"><span className="fw-semibold text-secondary small d-block">Gender:</span><span className="fw-bold text-dark capitalize">{patientData.gender || "N/A"}</span></div>
              </div>
            </div>
          </div>
        )}
        <div className="card">
          <div className="card-header border-bottom"><h5 className="mb-0">Edit Client Feedback &amp; Discharge Interview</h5><small className="text-muted">Day Rehab Programme (DRP)</small></div>
          <div className="card-body">
            {isLoading ? (
              <div className="d-flex justify-content-center py-5"><div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading...</span></div></div>
            ) : error ? (
              <div className="alert alert-danger m-4">{error}</div>
            ) : (
              <form onSubmit={handleSubmit}>
                <DrpClientFeedbackForm formData={formData} setFormData={setFormData} />
                <div className="row px-4 pb-4">
                  <div className="col-md-2 ms-auto">
                    <button type="submit" className="btn btn-primary w-100 sticky-form-submit"><i className="ri-save-3-line me-1"></i> Update</button>
                  </div>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default EditDrpClientFeedback;
